import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { formatDuration } from '../utils/workouts';
import './ActivityStreamsChart.css';

const PADDING = { top: 12, right: 44, bottom: 26, left: 44 };
const HEIGHT = 240;

// Линия поверх профиля высоты: выбирается вкладкой
const METRICS = [
  { key: 'power', unit: 'workouts.units.watts', className: 'activity-chart-line--power' },
  { key: 'heart_rate', unit: 'workouts.activity.units.bpm', className: 'activity-chart-line--hr' },
  { key: 'speed', unit: 'workouts.activity.units.kmh', className: 'activity-chart-line--speed' },
  { key: 'cadence', unit: 'workouts.activity.units.rpm', className: 'activity-chart-line--cadence' },
];

const niceStep = (span, count) => {
  const raw = span / Math.max(count, 1);
  const power = 10 ** Math.floor(Math.log10(raw || 1));
  return [1, 2, 5, 10].map((k) => k * power).find((step) => step >= raw) || raw;
};

const range = (values) => {
  const list = values.filter((value) => value !== null && value !== undefined);
  if (!list.length) return null;
  return [Math.min(...list), Math.max(...list)];
};

// Высота (заливка) и выбранная метрика (линия) вдоль дистанции, а у заезда
// без дистанции — вдоль времени. onHover отдает индекс точки телеметрии
const ActivityStreamsChart = ({ streams, onHover }) => {
  const { t } = useLanguage();
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  const available = METRICS.filter((metric) => range(streams[metric.key] || []));
  const [metricKey, setMetricKey] = useState(available[0]?.key || null);
  const metric = available.find((item) => item.key === metricKey) || available[0] || null;

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const byDistance = Boolean(range(streams.distance || [])?.[1]);
  const xs = useMemo(
    () => (byDistance ? streams.distance : streams.time) || [],
    [byDistance, streams],
  );
  const xRange = useMemo(() => range(xs) || [0, 1], [xs]);
  const plotWidth = Math.max(width - PADDING.left - PADDING.right, 1);
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const x = (value) => PADDING.left + ((value - xRange[0]) / ((xRange[1] - xRange[0]) || 1)) * plotWidth;
  const scaleY = (values, padTop = 0.1) => {
    const bounds = range(values);
    if (!bounds) return null;
    const span = bounds[1] - bounds[0] || 1;
    const low = Math.max(0, bounds[0] - span * 0.05);
    const high = bounds[1] + span * padTop;
    return { low, high, y: (value) => PADDING.top + plotHeight - ((value - low) / (high - low)) * plotHeight };
  };

  const altitude = streams.altitude || [];
  const altScale = scaleY(altitude, 0.6);
  const lineValues = metric ? streams[metric.key] : [];
  const lineScale = metric ? scaleY(lineValues) : null;

  const path = (values, scale) => {
    let d = '';
    let open = false;
    values.forEach((value, i) => {
      if (value === null || value === undefined || xs[i] === null || xs[i] === undefined) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${x(xs[i]).toFixed(1)},${scale.y(value).toFixed(1)}`;
      open = true;
    });
    return d;
  };

  const altPath = altScale ? path(altitude, altScale) : '';
  const altArea = altPath
    ? `${altPath}L${x(xRange[1]).toFixed(1)},${PADDING.top + plotHeight}L${x(xRange[0]).toFixed(1)},${PADDING.top + plotHeight}Z`
    : '';

  const xTicks = useMemo(() => {
    if (!width) return [];
    const span = xRange[1] - xRange[0];
    const step = byDistance ? niceStep(span / 1000, plotWidth / 80) * 1000 : niceStep(span / 60, plotWidth / 80) * 60;
    const ticks = [];
    for (let value = Math.ceil(xRange[0] / step) * step; value <= xRange[1]; value += step) ticks.push(value);
    return ticks;
  }, [width, xRange, byDistance, plotWidth]);

  const xLabel = (value) => (byDistance ? `${Math.round(value / 100) / 10}` : formatDuration(value));

  const pickIndex = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const target = xRange[0] + ((event.clientX - rect.left - PADDING.left) / plotWidth) * (xRange[1] - xRange[0]);
    let best = null;
    xs.forEach((value, i) => {
      if (value === null || value === undefined) return;
      if (best === null || Math.abs(value - target) < Math.abs(xs[best] - target)) best = i;
    });
    return best;
  };

  const move = (event) => {
    const index = pickIndex(event);
    setHover(index);
    onHover?.(index);
  };

  const leave = () => {
    setHover(null);
    onHover?.(null);
  };

  if (!xs.length || (!altScale && !lineScale)) return null;

  const value = (key, digits = 0) => {
    const v = hover !== null ? streams[key]?.[hover] : null;
    return v === null || v === undefined ? null : Number(v).toFixed(digits);
  };

  return (
    <div className="activity-chart">
      {available.length > 0 && (
        <div className="activity-chart-tabs" role="tablist">
          {available.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={metric?.key === item.key}
              className={`activity-chart-tab ${metric?.key === item.key ? 'activity-chart-tab--active' : ''}`}
              onClick={() => setMetricKey(item.key)}
            >
              {t(`workouts.activity.metrics.${item.key}`)}
            </button>
          ))}
        </div>
      )}

      <div ref={wrapRef} className="activity-chart-plot">
        {width > 0 && (
          <svg
            width={width}
            height={HEIGHT}
            onPointerMove={move}
            onPointerLeave={leave}
            role="img"
            aria-label={t('workouts.activity.chart')}
          >
            {xTicks.map((tick) => (
              <g key={tick}>
                <line className="activity-chart-grid" x1={x(tick)} x2={x(tick)} y1={PADDING.top} y2={PADDING.top + plotHeight} />
                <text className="activity-chart-axis" x={x(tick)} y={HEIGHT - 8} textAnchor="middle">{xLabel(tick)}</text>
              </g>
            ))}
            <text className="activity-chart-axis" x={width - 4} y={HEIGHT - 8} textAnchor="end">
              {byDistance ? t('workouts.activity.units.km') : ''}
            </text>

            {altScale && (
              <>
                <path className="activity-chart-altitude" d={altArea} />
                <text className="activity-chart-axis" x={PADDING.left - 6} y={altScale.y(altScale.high) + 10} textAnchor="end">
                  {Math.round(altScale.high)}
                </text>
                <text className="activity-chart-axis" x={PADDING.left - 6} y={PADDING.top + plotHeight} textAnchor="end">
                  {Math.round(altScale.low)} {t('workouts.activity.units.m')}
                </text>
              </>
            )}

            {lineScale && (
              <>
                <path className={`activity-chart-line ${metric.className}`} d={path(lineValues, lineScale)} />
                <text className="activity-chart-axis" x={width - PADDING.right + 6} y={lineScale.y(lineScale.high) + 10}>
                  {Math.round(lineScale.high)}
                </text>
                <text className="activity-chart-axis" x={width - PADDING.right + 6} y={PADDING.top + plotHeight}>
                  {Math.round(lineScale.low)}
                </text>
              </>
            )}

            {hover !== null && xs[hover] !== null && (
              <line className="activity-chart-cursor" x1={x(xs[hover])} x2={x(xs[hover])} y1={PADDING.top} y2={PADDING.top + plotHeight} />
            )}
          </svg>
        )}
      </div>

      <div className="activity-chart-readout" aria-live="polite">
        {hover !== null ? (
          <>
            {streams.distance?.[hover] !== undefined && streams.distance?.[hover] !== null && (
              <span>{(streams.distance[hover] / 1000).toFixed(2)} {t('workouts.activity.units.km')}</span>
            )}
            {streams.time?.[hover] !== undefined && streams.time?.[hover] !== null && <span>{formatDuration(streams.time[hover])}</span>}
            {value('altitude') !== null && <span>{value('altitude')} {t('workouts.activity.units.m')}</span>}
            {METRICS.map((item) => value(item.key, item.key === 'speed' ? 1 : 0) !== null && (
              <span key={item.key}>
                {t(`workouts.activity.metrics.${item.key}`)}: {value(item.key, item.key === 'speed' ? 1 : 0)} {t(item.unit)}
              </span>
            ))}
          </>
        ) : (
          // Без координат (заезды MyWhoosh, Zwift) карты нет — и подсказки про нее тоже
          streams.position && <span className="workouts-muted">{t('workouts.activity.chartHint')}</span>
        )}
      </div>
    </div>
  );
};

export default ActivityStreamsChart;
